import { Injectable } from '@angular/core';
import { Firestore, collection, collectionData, doc, setDoc, deleteDoc, query, where, orderBy, limit, CollectionReference, DocumentReference, docData } from '@angular/fire/firestore';
import { Gig } from '../models/gig.interface';
import { Job } from '../models/job.interface';
import { Review } from '../models/review.interface';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DatabaseProvider {
  private gigsRef: CollectionReference;
  private jobsRef: CollectionReference;
  private adsRef: CollectionReference;
  private volsRef: CollectionReference;

  constructor(private firestore: Firestore) {
    this.gigsRef = collection(this.firestore, 'ge_gigs');
    this.jobsRef = collection(this.firestore, 'ge_jobs');
    this.adsRef = collection(this.firestore, 'ge_ads');
    this.volsRef = collection(this.firestore, 'ge_vols');
  }

  getRecentGigs() {
    const gigsQuery = query(
      collection(this.firestore, 'ge_gigs'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );
    return collectionData(gigsQuery) as Observable<Gig[]>;
  }

  getUserGigs(uid: string) {
    const userGigsQuery = query(
      collection(this.firestore, 'ge_gigs'),
      orderBy('createdAt', 'desc'),
      where('uid', '==', uid),
      limit(10)
    );
    return collectionData(userGigsQuery) as Observable<Gig[]>;
  }

  createGig(uid: string, data: Gig) {
    const newDocRef = doc(this.gigsRef);
    const docData = { uid, ...data, id: newDocRef.id };
    return setDoc(newDocRef, docData);
  }

  deleteGig(id: string) {
    const gigDocRef = doc(this.firestore, `ge_gigs/${id}`);
    return deleteDoc(gigDocRef);
  }

  //// HEARTS ////

  createHeart(uid: string, gig: string) {
    const date = Math.floor(Date.now());
    const gigsterDocRef = doc(this.firestore, `ge_gigs/${gig}/gigsters/${uid}`);
    return setDoc(gigsterDocRef, { hearted: date }, { merge: true });
  }

  removeHeart(uid: string, gig: string) {
    const gigsterDocRef = doc(this.firestore, `ge_gigs/${gig}/gigsters/${uid}`);
    return setDoc(gigsterDocRef, { hearted: '' }, { merge: true });
  }

  createJobHeart(uid: string, job: string) {
    const date = Math.floor(Date.now());
    const jobsterDocRef = doc(this.firestore, `ge_jobs/${job}/hunters/${uid}`);
    return setDoc(jobsterDocRef, { hearted: date }, { merge: true });
  }

  removeJobHeart(uid: string, job: string) {
    const jobsterDocRef = doc(this.firestore, `ge_jobs/${job}/hunters/${uid}`);
    return setDoc(jobsterDocRef, { hearted: '' }, { merge: true });
  }

  createAdHeart(uid: string, ad: string) {
    const date = Math.floor(Date.now());
    const adsterDocRef = doc(this.firestore, `ge_ads/${ad}/hunters/${uid}`);
    return setDoc(adsterDocRef, { hearted: date }, { merge: true });
  }

  removeAdHeart(uid: string, ad: string) {
    const adsterDocRef = doc(this.firestore, `ge_ads/${ad}/hunters/${uid}`);
    return setDoc(adsterDocRef, { hearted: '' }, { merge: true });
  }

  createVolHeart(uid: string, vol: string) {
    const date = Math.floor(Date.now());
    const volsterDocRef = doc(this.firestore, `ge_vols/${vol}/volunteers/${uid}`);
    return setDoc(volsterDocRef, { hearted: date }, { merge: true });
  }

  removeVolHeart(uid: string, vol: string) {
    const volsterDocRef = doc(this.firestore, `ge_vols/${vol}/volunteers/${uid}`);
    return setDoc(volsterDocRef, { hearted: '' }, { merge: true });
  }

  //// GIGSTERS INTERACTION WITH GIGS ////

  gigSters(uid: string, gig: string) {
    const gigsterDocRef = doc(this.firestore, `ge_gigs/${gig}/gigsters/${uid}`);
    return docData(gigsterDocRef);
  }

  jobSters(uid: string, job: string) {
    const jobsterDocRef = doc(this.firestore, `ge_jobs/${job}/hunters/${uid}`);
    return docData(jobsterDocRef);
  }

  adSters(uid: string, ad: string) {
    const adsterDocRef = doc(this.firestore, `ge_ads/${ad}/hunters/${uid}`);
    return docData(adsterDocRef);
  }

  volSters(uid: string, vol: string) {
    const volsterDocRef = doc(this.firestore, `ge_vols/${vol}/volunteers/${uid}`);
    return docData(volsterDocRef);
  }

  //// APPLY ////

  applyGig(uid: string, gig: string) {
    const date = Math.floor(Date.now());
    const gigsterDocRef = doc(this.firestore, `ge_gigs/${gig}/gigsters/${uid}`);
    return setDoc(gigsterDocRef, { applied: date, uid: uid }, { merge: true });
  }

  withdrawGig(uid: string, gig: string) {
    const gigsterDocRef = doc(this.firestore, `ge_gigs/${gig}/gigsters/${uid}`);
    return setDoc(gigsterDocRef, { applied: '', uid: uid }, { merge: true });
  }

  //// PROMOTE APPLICATIONS ////

  adHunters(uid: string, ad: string) {
    const hunterDocRef = doc(this.firestore, `ge_ads/${ad}/hunters/${uid}`);
    return docData(hunterDocRef);
  }

  //// APPLY ////

  applyAd(customer: string, ad: string, uid: string, message: string) {
    const date = Math.floor(Date.now());
    const hunterDocRef = doc(this.firestore, `ge_ads/${ad}/hunters/${uid}`);
    return setDoc(hunterDocRef, { applied: date, customer: customer, message: message, uid: uid }, { merge: true });
  }

  withdrawAd(uid: string, ad: string) {
    const hunterDocRef = doc(this.firestore, `ge_ads/${ad}/hunters/${uid}`);
    return setDoc(hunterDocRef, { applied: '', uid: uid }, { merge: true });
  }

  //// JOB APPLICATIONS ////

  jobHunters(uid: string, job: string) {
    const hunterDocRef = doc(this.firestore, `ge_jobs/${job}/hunters/${uid}`);
    return docData(hunterDocRef);
  }

  //// APPLY ////

  applyJob(customer: string, job: string, uid: string, message: string) {
    const date = Math.floor(Date.now());
    const hunterDocRef = doc(this.firestore, `ge_jobs/${job}/hunters/${uid}`);
    return setDoc(hunterDocRef, { applied: date, customer: customer, message: message, uid: uid }, { merge: true });
  }

  withdrawJob(uid: string, job: string) {
    const hunterDocRef = doc(this.firestore, `ge_jobs/${job}/hunters/${uid}`);
    return setDoc(hunterDocRef, { applied: '', uid: uid }, { merge: true });
  }

  //// VOLUNTEERS ////

  volunteers(uid: string, vol: string) {
    const hunterDocRef = doc(this.firestore, 'ge_vols', vol, 'volunteers', uid);
    return docData(hunterDocRef);
  }

  //// APPLY ////

  applyVol(customer: string, vol: string, uid: string, message: string) {
    const date = Math.floor(Date.now());
    const hunterDocRef = doc(this.firestore, `ge_vols/${vol}/hunters/${uid}`);
    return setDoc(hunterDocRef, { applied: date, customer: customer, message: message, uid: uid }, { merge: true });
  }

  withdrawVol(uid: string, vol: string) {
    const hunterDocRef = doc(this.firestore, `ge_vols/${vol}/hunters/${uid}`);
    return setDoc(hunterDocRef, { applied: '', uid: uid }, { merge: true });
  }

  //// RELATIONSHIPS ////

  getUsers() {
    const usersQuery = query(
      collection(this.firestore, 'ge_users'),
      limit(10)
    );
    return collectionData(usersQuery);
  }

  follow(followerId: string, followedId: string) {
    const docId = this.concatIds(followerId, followedId);
    const bidDocRef = doc(this.firestore, `ge_bids/${docId}`);
    
    const data = {
      followerId,
      followedId
    };

    return setDoc(bidDocRef, data);
  }

  unfollow(followerId: string, followedId: string) {
    const docId = this.concatIds(followerId, followedId);
    const bidDocRef = doc(this.firestore, `ge_bids/${docId}`);
    
    return deleteDoc(bidDocRef);
  }

  isFollowing(followerId: string, followedId: string) {
    const docId = this.concatIds(followerId, followedId);
    const bidDocRef = doc(this.firestore, `ge_bids/${docId}`);
    
    return docData(bidDocRef);
  }

  // Helper to format the docId for relationships
  private concatIds(a: string, b: string) {
    return `${a}_${b}`;
  }
}
